import { Box } from "@mui/material"
import { useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import type { QuerySubState } from "@reduxjs/toolkit/query"
import type { BaseEndpointDefinition } from "@reduxjs/toolkit/query"

import type { EntityType } from "types/common.types"
import type { RootState } from "features/store"

import useActivityDateRange from "helpers/hooks/useActivityDateRange"
import AlertsTable from "widgets/alerts/AlertsTable"
import DateRangeCustomSelect from "widgets/dates/DateRangeCustomSelect"

// Only drives which options the picker offers (and its date constraints), never
// the selected range: activity shows past events, so every entity gets the
// non-forecast options and no future dates.
const PICKER_OPTIONS_ENTITY: EntityType = "device"

interface ActivityPanelProps {
  devicesIds?: number[]
  groupId?: number
}

const ActivityPanel = ({ devicesIds, groupId }: ActivityPanelProps) => {
  const { t } = useTranslation()
  const { dateRange, dateRangeDateTime, setDateReference, updateCustomDateRange } =
    useActivityDateRange()

  const isFetching = useSelector((state: RootState) => {
    const queries = Object.values(state.api.queries)
    return queries.some(
      (query: QuerySubState<BaseEndpointDefinition<any, any, any>> | undefined) =>
        query?.status === "pending",
    )
  })

  const emptyContent = (
    <Box sx={{ textAlign: "center", color: "text.secondary" }}>
      <Box>{t("alerts.NO_EVENTS_IN_RANGE")}</Box>
      <Box>{t("alerts.ADJUST_RANGE_HINT")}</Box>
    </Box>
  )

  return (
    <AlertsTable
      dateRange={dateRange}
      isDateRangeValid
      devicesIds={devicesIds}
      groupId={groupId}
      emptyContent={emptyContent}
      toolbarControl={
        <DateRangeCustomSelect
          entity={PICKER_OPTIONS_ENTITY}
          dateRangeDateTime={dateRangeDateTime}
          isFetching={isFetching}
          setSelectedDateReference={setDateReference}
          updateDateRange={updateCustomDateRange}
        />
      }
    />
  )
}

export default ActivityPanel
